// @flow
import is from './is'

// Types
type Options = {
  string?: boolean,
  number?: boolean,
  object?: boolean,
  array?: boolean,
  falsy?: boolean,
}

/**
 * Function that helps to remove empty fields from given object.
 * The second argument defines which types should be considered
 * as empty values. By default only `null` and `undefined` considered
 * as empty value. You can pass `{ string: true }` to consider empty
 * strings as empty value as well, or `{ number: true }` to remove
 * fields with `0` value.
 *
 * @param {Object} obj  Object where you want to omit empty values
 * @param {Options} options
 */
function omitEmptyFields<T: Object>(obj: T, options?: Options): $Shape<T> {
  if (!obj) return {}
  const keys = Object.keys(obj)
  const {
    string = false,
    number = false,
    object = false,
    array = false,
    falsy = false,
  } = options || {}

  return keys.reduce((acc, k) => {
    const val = obj[k]
    if (
      is.undef(val) ||
      (falsy && val === false) ||
      (string && is.string(val) && !val.trim()) ||
      (number && val === 0) ||
      (array && is.array(val) && val.length === 0) ||
      (object && is.object(val) && Object.keys((val: any)).length === 0)
    ) {
      return acc
    }

    acc[k] = val
    return acc
  }, {})
}

export default omitEmptyFields
